import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import MainLayout from '../../components/layout/MainLayout';
import MetricCard from './components/MetricCard';
import UsageMetric from './components/UsageMetric';
import QuickActionCard from './components/QuickActionCard';
import RecentActivityTable from './components/RecentActivityTable';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../hooks';
import {
  getRecentUniqueAlerts,
  getCandidatesStats,
  getAlertsStats,
  getCandidatesUpdatedLast7Days,
  getUserCallStats,
} from '../../lib/api';
import { isZohoAtsConfigured } from '../../lib/utils/ats';
import { planHasAtsIntegration, planHasVoiceAI } from '../../lib/utils/planEntitlements';
import { transformAlert } from '../../lib/utils/alertLabels';

const Dashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const { profile, loading: profileLoading } = useProfile();
  const [recentAlertsData, setRecentAlertsData] = useState([]);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [candidatesStats, setCandidatesStats] = useState({ total: 0 });
  const [alertsStats, setAlertsStats] = useState({ total: 0 });
  const [updatedCandidatesCount, setUpdatedCandidatesCount] = useState(0);
  const [callStats, setCallStats] = useState({ calls_duration: 0, minutes_limit: 0 });
  const hasLoadedDashboardRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setLoadingDashboard(false);
        hasLoadedDashboardRef.current = false;
        return;
      }

      const useSilent = hasLoadedDashboardRef.current;

      try {
        if (!useSilent) setLoadingDashboard(true);

        const [recentAlerts, candStats, alertStats, updatedCount, calls] = await Promise.all([
          getRecentUniqueAlerts(userId, 10),
          getCandidatesStats(userId),
          getAlertsStats(userId),
          getCandidatesUpdatedLast7Days(userId),
          getUserCallStats(userId),
        ]);

        setRecentAlertsData(recentAlerts || []);
        setCandidatesStats(candStats || { total: 0 });
        setAlertsStats(alertStats || { total: 0 });
        setUpdatedCandidatesCount(updatedCount || 0);
        setCallStats(calls || { calls_duration: 0, minutes_limit: 0 });
        hasLoadedDashboardRef.current = true;
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        if (!useSilent) setLoadingDashboard(false);
      }
    };

    fetchData();
  }, [userId]);

  const recentActivities = useMemo(
    () => recentAlertsData.map(transformAlert),
    [recentAlertsData],
  );

  const metrics = useMemo(() => [
    {
      id: 1,
      title: t('dashboard.candidatesUpdated'),
      value: String(updatedCandidatesCount || 0),
      icon: 'Users',
      trend: t('dashboard.totalCandidates', { count: candidatesStats.total || 0 }),
      isNew: updatedCandidatesCount > 0,
    },
    {
      id: 2,
      title: t('dashboard.newAlerts'),
      value: String(alertsStats.total || 0),
      icon: 'Bell',
      trend: alertsStats.total > 0
        ? t('dashboard.openToWorkAlerts', { count: alertsStats.open_to_work || 0 })
        : t('dashboard.noNewAlerts'),
      isNew: alertsStats.total > 0,
    },
    {
      id: 3,
      title: t('dashboard.identifiedSeekers'),
      value: String(candidatesStats.activeSeekers || 0),
      secondaryValue: candidatesStats.potentialSeekers || 0,
      secondaryLabel:
        (candidatesStats.potentialSeekers || 0) === 1
          ? t('dashboard.potential')
          : t('dashboard.potentials'),
      icon: 'Target',
      trend: t('dashboard.totalCandidates', { count: candidatesStats.total || 0 }),
    },
  ], [candidatesStats, alertsStats, updatedCandidatesCount, t]);

  const plan = profile?.subscription_plan?.toLowerCase() || null;
  const extraMinutes = Math.max(0, Number(profile?.extra_minutes) || 0);

  const usageMetrics = useMemo(() => {
    const maxCandidatesValue = profile?.max_candidates || 50;
    const remaining = profile?.candidates_remaining != null
      ? Math.max(0, profile.candidates_remaining)
      : Math.max(0, maxCandidatesValue - (candidatesStats.total || 0));
    const items = [
      {
        id: 1,
        title: t('dashboard.uploadLimit'),
        current: candidatesStats.total || 0,
        total: maxCandidatesValue,
        unit: 'candidates',
        color: 'primary',
        footnote: t('dashboard.remaining', { count: remaining }),
      },
    ];
    if (planHasVoiceAI(plan)) {
      items.push({
        id: 2,
        title: t('dashboard.monthlyMinutes'),
        current: callStats.calls_duration || 0,
        total: (callStats.minutes_limit || 0) + extraMinutes,
        unit: 'minutes',
        color: 'warning',
        footnote: extraMinutes > 0
          ? t('dashboard.extraMinutesNote', { count: extraMinutes })
          : null,
      });
    }
    return items;
  }, [plan, candidatesStats.total, profile?.max_candidates, profile?.candidates_remaining, callStats, extraMinutes, t]);

  const hasVoiceAI = planHasVoiceAI(plan);
  const hasAtsIntegration = planHasAtsIntegration(plan);
  const isZohoAtsConnected = isZohoAtsConfigured(profile);
  const isBookingConfigured = Boolean(profile?.booking_type?.trim?.());
  const maxCandidates = profile?.max_candidates || 50;
  const remainingCandidates = profile?.candidates_remaining != null
    ? Math.max(0, profile.candidates_remaining)
    : Math.max(0, maxCandidates - (candidatesStats.total || 0));
  const isCandidateLimitReached = remainingCandidates <= 0;

  const quickActions = useMemo(() => {
    const actions = [
      {
        id: 1,
        title: t('dashboard.importCandidates'),
        description: t('dashboard.importDescription'),
        icon: 'Download',
        actionText: t('dashboard.importNow'),
        route: '/import-candidates',
        variant: 'default',
      },
      {
        id: 2,
        title: t('dashboard.connectAts'),
        description: hasAtsIntegration
          ? t('dashboard.connectAtsReady')
          : t('dashboard.connectAtsUpgrade'),
        icon: 'Link',
        actionText: hasAtsIntegration ? t('dashboard.setupIntegration') : t('common.upgradePlan'),
        route: hasAtsIntegration ? '/connect-ats' : '/billing',
        variant: 'outline',
      },
      {
        id: 3,
        title: t('dashboard.setupAi'),
        description: hasVoiceAI
          ? t('dashboard.setupAiReady')
          : t('dashboard.setupAiUpgrade'),
        icon: 'Bot',
        actionText: hasVoiceAI ? t('dashboard.configureAi') : t('common.upgradePlan'),
        route: hasVoiceAI ? '/ai-assistant/setup' : '/billing',
        variant: 'outline',
      },
    ];

    return actions.filter((action) => {
      if ((isCandidateLimitReached || isZohoAtsConnected) && action.id === 1) return false;
      if (isZohoAtsConnected && action.id === 2) return false;
      if (isBookingConfigured && action.id === 3) return false;
      return true;
    });
  }, [hasAtsIntegration, hasVoiceAI, isZohoAtsConnected, isBookingConfigured, isCandidateLimitReached, t]);

  if (loadingDashboard || (profileLoading && !profile)) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{t('dashboard.loading')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8 py-6 md:py-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground">
            {t('dashboard.title')}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {t('dashboard.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {metrics.map((metric) => (
            <MetricCard
              key={metric.id}
              title={metric.title}
              value={metric.value}
              secondaryValue={metric.secondaryValue}
              secondaryLabel={metric.secondaryLabel}
              icon={metric.icon}
              trend={metric.trend}
              isNew={metric.isNew}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {usageMetrics.map((usage) => (
            <UsageMetric
              key={usage.id}
              title={usage.title}
              current={usage.current}
              total={usage.total}
              unit={usage.unit}
              color={usage.color}
              footnote={usage.footnote}
            />
          ))}
        </div>

        {quickActions.length > 0 && (
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-4 md:mb-6">
              {t('dashboard.quickActions')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {quickActions.map((action) => (
                <QuickActionCard
                  key={action.id}
                  title={action.title}
                  description={action.description}
                  icon={action.icon}
                  actionText={action.actionText}
                  route={action.route}
                  variant={action.variant}
                />
              ))}
            </div>
          </div>
        )}

        <RecentActivityTable activities={recentActivities} loading={loadingDashboard} />
      </div>
    </MainLayout>
  );
};

export default Dashboard;
